<template>
  <div class="company-details-page">
    <!-- Loading spinner -->
    <div v-if="loading" class="text-center py-5">
      <BSpinner variant="primary" />
    </div>

    <!-- Contenu principal -->
    <div v-else-if="company">
      <!-- 1. Header avec nom et boutons -->
      <div class="company-header-card">
        <div class="d-flex justify-content-between align-items-center">
          <div class="company-info d-flex align-items-center">
            <div class="icon-wrapper me-3">
              <i class="ri-building-line"></i>
            </div>
            <div>
              <div class="company-label">Fiche Entreprise</div>
              <div class="info-value">{{ company.legalName }}</div>
            </div>
          </div>
          <div class="action-buttons">
            <button
              type="button"
              class="list-btn list-btn--primary"
              data-testid="company-details-new-survey"
              @click="startSurvey"
            >
              <i class="ri-file-add-line"></i>
              Nouvelle enquête
            </button>
            <button
              type="button"
              class="list-btn list-btn--ghost"
              @click="editCompany"
            >
              <i class="ri-edit-line"></i>
              Modifier
            </button>
            <button
              type="button"
              class="list-btn list-btn--ghost"
              title="Exporter la fiche en Excel"
              data-testid="company-details-export-excel"
              :disabled="exportingExcel"
              @click="exportFicheExcel"
            >
              <i class="ri-file-excel-line"></i>
              {{ exportingExcel ? 'Export…' : 'Exporter en Excel' }}
            </button>
            <button
              type="button"
              class="list-btn list-btn--ghost"
              title="Exporter la fiche en PDF"
              data-testid="company-details-export-pdf"
              :disabled="exportingPdf"
              @click="exportFichePdf"
            >
              <i class="ri-file-pdf-line"></i>
              {{ exportingPdf ? 'Export…' : 'Exporter en PDF' }}
            </button>
            <button
              type="button"
              class="list-btn list-btn--danger"
              @click="confirmDelete"
            >
              <i class="ri-delete-bin-line"></i>
              Supprimer
            </button>
            <button type="button" class="list-back" @click="goBack">
              <i class="ri-arrow-left-line"></i>
              Retour à la liste
            </button>
          </div>
        </div>
      </div>

      <!-- 2. Informations de l'entreprise -->
      <div class="company-info-card">
        <div class="section-header">
          <i class="ri-file-list-3-line"></i>
          <span>Informations générales</span>
        </div>

        <BRow class="info-grid">
          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-building-line"></i>
                Raison sociale
              </div>
              <div class="info-value">{{ company.legalName }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-briefcase-line"></i>
                Secteur d'activité principal
              </div>
              <div class="info-value">{{ company.mainActivitySector }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-building-2-line"></i>
                Secteur secondaire
              </div>
              <div class="info-value">{{ company.secondarySector || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-map-pin-line"></i>
                Gouvernorat
              </div>
              <div class="info-value">{{ company.governorate || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-map-pin-2-line"></i>
                Adresse
              </div>
              <div class="info-value">{{ company.address || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-mail-line"></i>
                Email
              </div>
              <div class="info-value">{{ company.email || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-global-line"></i>
                Site web
              </div>
              <div class="info-value">{{ company.website || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-calendar-event-line"></i>
                Année de création
              </div>
              <div class="info-value">{{ company.yearFounded || '-' }}</div>
            </div>
          </BCol>

          <BCol md="6" class="mb-3">
            <div class="info-item">
              <div class="info-label">
                <i class="ri-calendar-line"></i>
                Date d'ajout
              </div>
              <div class="info-value">{{ formatDate(company.createdAt) }}</div>
            </div>
          </BCol>
        </BRow>
      </div>

      <!-- 3. Historique des enquêtes -->
      <div class="company-surveys-card">
        <div class="section-header">
          <i class="ri-calendar-line"></i>
          <span>Historique des enquêtes</span>
        </div>

        <!--
          La comparaison se lance depuis l'historique lui-meme: c'est la que les
          enquetes de l'entreprise sont listees, et choisir « les deux que je
          veux comparer » n'a de sens qu'en face de cette liste.
        -->
        <div
          v-if="company.submissions && company.submissions.length > 0"
          class="surveys-compare-bar"
        >
          <button
            type="button"
            class="list-btn list-btn--primary"
            data-testid="company-surveys-compare"
            :disabled="!canCompare"
            @click="compareSelectedSurveys"
          >
            <i class="ri-git-compare-line"></i>
            Comparer
          </button>
          <span
            class="surveys-compare-bar__hint"
            data-testid="company-surveys-compare-hint"
          >
            {{ compareHint }}
          </span>
        </div>

        <div
          v-if="!company.submissions || company.submissions.length === 0"
          class="text-center py-4"
        >
          <i class="ri-inbox-line" style="font-size: 3rem; color: #ccc"></i>
          <p class="text-muted mt-2">Aucune enquête pour le moment</p>
        </div>

        <BTable
          v-else
          :items="company.submissions"
          :fields="surveyFields"
          responsive
          class="surveys-table mb-0"
        >
          <template #cell(select)="{ item }">
            <input
              type="checkbox"
              class="survey-select"
              :data-testid="`company-survey-select-${item.id}`"
              :aria-label="`Sélectionner l'enquête ${item.id} pour la comparaison`"
              :checked="isSurveySelected(item.id)"
              @change="toggleSurveySelection(item.id)"
            />
          </template>

          <template #cell(id)="{ item }">
            <span class="survey-id">{{ item.id.substring(0, 13) }}</span>
          </template>

          <template #cell(status)="{ item }">
            <BBadge
              :variant="getStatusVariant(item.status)"
              class="status-badge"
            >
              {{ getStatusLabel(item.status) }}
            </BBadge>
          </template>

          <template #cell(startedAt)="{ item }">
            {{ formatDate(item.startedAt) }}
          </template>

          <template #cell(submittedAt)="{ item }">
            {{ item.submittedAt ? formatDate(item.submittedAt) : '-' }}
          </template>

          <template #cell(actions)="{ item }">
            <BButton
              size="sm"
              variant="link"
              @click="viewSurvey(item.id)"
              class="row-action"
            >
              <i class="ri-eye-line"></i> Voir
            </BButton>
          </template>
        </BTable>
      </div>
    </div>

    <!-- Erreur -->
    <div v-else class="text-center py-5">
      <i
        class="ri-error-warning-line"
        style="font-size: 3rem; color: #e74c3c"
      ></i>
      <p class="text-danger mt-2">Entreprise introuvable</p>
    </div>

    <!-- Edit Company Modal -->
    <EditCompanyModal
      v-model="editModal"
      :company="company"
      @updated="onCompanyUpdated"
    />

    <!-- Delete Company Modal -->
    <DeleteCompanyModal
      v-model="deleteModal"
      :company="company"
      @deleted="onCompanyDeleted"
    />
  </div>
</template>

<script>
import { companyService } from '@/shared/services/company.service';
import notificationService from '@/shared/services/notification.service';
import EditCompanyModal from '@/components/companies/EditCompanyModal.vue';
import DeleteCompanyModal from '@/components/companies/DeleteCompanyModal.vue';

export default {
  name: 'CompanyDetails',
  components: {
    EditCompanyModal,
    DeleteCompanyModal,
  },
  data() {
    return {
      company: null,
      loading: false,
      editModal: false,
      deleteModal: false,
      // Deux drapeaux distincts: chaque bouton ne doit refleter que sa propre
      // requete, exporter le PDF ne bloque pas l'export Excel.
      exportingExcel: false,
      exportingPdf: false,
      // Les enquetes cochees en vue d'une comparaison, dans l'ordre ou elles
      // ont ete cochees - l'ordre sert a savoir laquelle lacher au 3e clic.
      selectedSurveyIds: [],
      droppedSurveySelection: false,
      surveyFields: [
        { key: 'select', label: '' },
        { key: 'id', label: 'N° Enquête' },
        { key: 'status', label: 'Statut' },
        { key: 'startedAt', label: 'Date de création' },
        { key: 'submittedAt', label: 'Dernière mise à jour' },
        { key: 'actions', label: 'Actions' },
      ],
    };
  },
  computed: {
    /** Une comparaison se fait a deux: ni une enquete seule, ni trois. */
    canCompare() {
      return this.selectedSurveyIds.length === 2;
    },

    compareHint() {
      if (this.droppedSurveySelection) {
        return 'Deux enquêtes au maximum : la plus ancienne sélection a été retirée.';
      }
      if (this.canCompare) {
        return 'Deux enquêtes sélectionnées.';
      }
      return 'Sélectionnez deux enquêtes à comparer';
    },
  },
  mounted() {
    this.fetchCompany();
  },
  methods: {
    isSurveySelected(id) {
      return this.selectedSurveyIds.includes(id);
    },

    /**
     * Au troisieme clic, c'est la selection la plus ancienne qui tombe, et le
     * message le dit. L'autre option - refuser le clic - laisse l'utilisateur
     * devant une case qui ne se coche pas sans savoir laquelle decocher; ici la
     * case cliquee se coche toujours, et la case liberee se voit se decocher.
     */
    toggleSurveySelection(id) {
      if (this.isSurveySelected(id)) {
        this.selectedSurveyIds = this.selectedSurveyIds.filter(
          (selected) => selected !== id,
        );
        this.droppedSurveySelection = false;
        return;
      }

      const next = [...this.selectedSurveyIds, id];
      this.droppedSurveySelection = next.length > 2;
      this.selectedSurveyIds = next.slice(-2);
    },

    /**
     * La comparaison est une page a part entiere, pas une fenetre: elle est
     * longue, elle se fait defiler, elle doit pouvoir etre partagee par son
     * adresse et imprimee.
     */
    compareSelectedSurveys() {
      if (!this.canCompare) return;

      const [a, b] = this.selectedSurveyIds;
      this.$router.push({
        name: 'company-survey-comparison',
        params: { id: this.$route.params.id },
        query: { a, b },
      });
    },

    async fetchCompany() {
      this.loading = true;
      try {
        this.company = await companyService.getCompany(this.$route.params.id);
      } catch (error) {
        notificationService.toastError(
          "Impossible de charger les informations de l'entreprise",
        );
        console.error('Error fetching company:', error);
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      this.$router.push('/companies');
    },

    /**
     * L'entreprise est sous les yeux: son identite n'a pas a etre resaisie
     * dans le questionnaire. Le formulaire la reprend depuis l'identifiant.
     */
    startSurvey() {
      this.$router.push({
        name: 'Survey',
        query: { companyId: this.$route.params.id },
      });
    },

    editCompany() {
      this.editModal = true;
    },

    onCompanyUpdated(updatedCompany) {
      this.company = updatedCompany;
      this.editModal = false;
    },

    confirmDelete() {
      this.deleteModal = true;
    },

    onCompanyDeleted() {
      this.$router.push('/companies');
    },

    /**
     * Les deux exports partagent tout sauf le service appele et l'extension:
     * le telechargement passe par le meme chemin que celui de la liste des
     * entreprises (URL d'objet, ancre, revocation), un seul a maintenir.
     */
    async downloadFiche({ request, extension, flag }) {
      if (this[flag]) {
        return;
      }

      this[flag] = true;

      try {
        const response = await request();
        const blob = response?.data ?? response;
        const filename =
          this.filenameFromHeaders(response?.headers) ||
          this.fallbackFilename(extension);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        notificationService.toastError(
          "Erreur lors de l'export de la fiche entreprise.",
        );
        console.error('exportFiche error:', error);
      } finally {
        // Toujours rendre le bouton: un echec ne doit pas le figer sur
        // « Export… » et interdire une nouvelle tentative.
        this[flag] = false;
      }
    },

    exportFicheExcel() {
      return this.downloadFiche({
        request: () => companyService.exportCompanyFiche(this.$route.params.id),
        extension: 'xlsx',
        flag: 'exportingExcel',
      });
    },

    exportFichePdf() {
      return this.downloadFiche({
        request: () =>
          companyService.exportCompanyFichePdf(this.$route.params.id),
        extension: 'pdf',
        flag: 'exportingPdf',
      });
    },

    /**
     * Le nom voulu est celui que l'API pose dans `Content-Disposition`.
     * On ne peut pas s'y fier: l'API est sur une autre origine, et l'en-tete
     * n'est lisible que si elle est explicitement exposee (`Access-Control-
     * Expose-Headers`). D'ou la reconstruction locale plus bas.
     */
    filenameFromHeaders(headers) {
      const disposition =
        headers?.['content-disposition'] || headers?.['Content-Disposition'];
      if (!disposition) return null;

      // `filename*=UTF-8''…` prime: c'est la forme qui porte les accents.
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      if (encoded) {
        try {
          return decodeURIComponent(encoded[1].trim());
        } catch {
          return encoded[1].trim();
        }
      }

      const plain = /filename="?([^";]+)"?/i.exec(disposition);
      return plain ? plain[1].trim() : null;
    },

    /** entreprise-<raison sociale slugifiee>-<aaaa-mm-jj>.<extension> */
    fallbackFilename(extension) {
      const date = new Date().toISOString().slice(0, 10);
      return `entreprise-${this.slugify(this.company?.legalName)}-${date}.${extension}`;
    },

    slugify(value) {
      const slug = (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return slug || 'sans-nom';
    },

    viewSurvey(surveyId) {
      this.$router.push(`/surveys/${surveyId}`);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    getStatusVariant(status) {
      const variants = {
        DRAFT: 'warning',
        IN_PROGRESS: 'info',
        SUBMITTED: 'success',
      };
      return variants[status] || 'secondary';
    },

    getStatusLabel(status) {
      const labels = {
        DRAFT: 'En attente',
        IN_PROGRESS: 'En cours',
        SUBMITTED: 'Complétée',
      };
      return labels[status];
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/pages/company-details';
</style>
